import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { MdClose } from "react-icons/md";
import { HiMenuAlt2 } from "react-icons/hi";
import { motion } from "framer-motion";
import { logo, logoLight } from "../../../assets/images";
import Image from "../../designLayouts/Image";
import { navBarList } from "../../../constants";
import Flex from "../../designLayouts/Flex";
import { 
  FaFacebook, FaYoutube, FaLinkedin, FaWhatsapp, 
  FaPhoneVolume, FaEnvelope, FaInstagram 
} from "react-icons/fa";

const Header = () => {
  const [showMenu, setShowMenu] = useState(true);
  const [sidenav, setSidenav] = useState(false);
  const [category, setCategory] = useState(false);
  const [brand, setBrand] = useState(false);
  const location = useLocation();

  // Responsive detection
  useEffect(() => {
    const ResponsiveMenu = () => setShowMenu(window.innerWidth >= 667);
    ResponsiveMenu();
    window.addEventListener("resize", ResponsiveMenu);
  }, []);

  // Social Icon Styles
  const iconStyles = {
    facebook: { color: "#0866FF", fontSize: "1.4em" },
    youtube: { color: "#FF0000", fontSize: "1.4em" },
    linkedin: { color: "#0A66C2", fontSize: "1.4em" },
    whatsapp: { color: "#40C351", fontSize: "1.4em" },
    instagram: { color: "#E1306C", fontSize: "1.4em" }
  };

  // Google Translate
  const googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_element"
    );
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    document.body.appendChild(script);
    window.googleTranslateElementInit = googleTranslateElementInit;
  }, []);

  return (
    <header className="w-full bg-white sticky top-0 z-50 border-b border-gray-200 shadow-sm">
      {/* Google Translate */}
      <div id="google_element"></div>

      {/* Top Header Section */}
      <div className="bg-[#F5F5F3] py-2">
        <div className="max-w-container mx-auto flex flex-col md:flex-row px-4 justify-between items-center">
          
          {/* Contact Info */}
          <div className="text-left flex items-center gap-3 text-black">
            <FaPhoneVolume />
            <a href="tel:+919860968617" className="phonenumberHeader">
              +91 98609 68617
            </a> / 
            <a href="tel:+918888804149" className="phonenumberHeader">
              +91 88888 04149
            </a>
            <span className="flex items-center gap-2 hidden max-[600px]:hidden md:flex">
              <FaEnvelope />
              <a href="mailto:sales@rkglobaltrader.com" className="mailHeader">
                sales@rkglobaltrader.com
              </a>
            </span>
          </div>

          {/* Social Media Icons */}
          <div className="hidden max-[600px]:hidden md:flex items-center gap-3">
            <span className="text-sm">Follow us:</span>

            <a 
              href="https://www.youtube.com/channel/UCEF5TbZReaHEcHKs36ceqXw"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit RK Global Trader YouTube channel"
            >
              <FaYoutube style={iconStyles.youtube} />
            </a>

            <a 
              href="https://www.facebook.com/profile.php?id=61554237851124"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit RK Global Trader Facebook page"
            >
              <FaFacebook style={iconStyles.facebook} />
            </a>

            <a 
              href="https://www.linkedin.com/company/rkglobaltraderexportimport"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit RK Global Trader LinkedIn page"
            >
              <FaLinkedin style={iconStyles.linkedin} />
            </a>

            <a 
              href="https://www.instagram.com/rkglobaltrader_export_import/"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit RK Global Trader Instagram page"
            >
              <FaInstagram style={iconStyles.instagram} />
            </a>

            <a 
              href="https://wa.me/+919860968617"
              target="_blank"
              rel="noreferrer"
              aria-label="Chat on WhatsApp"
            >
              <FaWhatsapp style={iconStyles.whatsapp} />
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav className="max-w-container mx-auto px-4">
        <Flex className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" aria-label="RK Global Trader - Home">
            <Image className="w-36 object-cover" imgSrc={logo} />
          </Link>

          {/* Desktop Menu */}
          {showMenu && (
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="flex items-center gap-4"
            >
              {navBarList.map(({ _id, title, link }) => (
                <NavLink
                  key={_id}
                  to={link}
                  state={{ data: location.pathname.split("/")[1] }}
                  className="text-base text-gray-600 hover:text-black hover:underline underline-offset-4"
                  aria-label={`Navigate to ${title} page`}
                >
                  {title}
                </NavLink>
              ))}
            </motion.div>
          )}

          {/* Mobile Menu Icon */}
          <HiMenuAlt2
            onClick={() => setSidenav(true)}
            className="md:hidden cursor-pointer w-8 h-6"
            aria-label="Open navigation menu"
          />

          {/* Mobile Side Navigation */}
          {sidenav && (
            <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-80 z-50">
              <motion.div
                initial={{ x: -200 }}
                animate={{ x: 0 }}
                className="w-[80%] h-full bg-primeColor p-6"
              >
                <img src={logoLight} alt="RK Global Trader Logo" className="w-32 mb-6" />

                {/* Mobile Nav Links */}
                <ul className="flex flex-col gap-4 text-white text-lg">
                  {navBarList.map((item) => (
                    <li key={item._id}>
                      <NavLink
                        to={item.link}
                        onClick={() => setSidenav(false)}
                        aria-label={`Go to ${item.title}`}
                      >
                        {item.title}
                      </NavLink>
                    </li>
                  ))}
                </ul>

                {/* Category Section */}
                <div className="mt-4">
                  <h1
                    onClick={() => setCategory(!category)}
                    className="flex justify-between cursor-pointer text-white text-lg"
                    aria-label="Toggle Trade by Category"
                  >
                    Trade by Category <span>{category ? "-" : "+"}</span>
                  </h1>

                  {category && (
                    <motion.ul
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-white ml-2 mt-2"
                    >
                      <li>
                        <Link to="/trade" onClick={() => setSidenav(false)}>
                          Agriculture Products
                        </Link>
                      </li>
                      <li>
                        <Link to="/" onClick={() => setSidenav(false)}>
                          Others
                        </Link>
                      </li>
                    </motion.ul>
                  )}
                </div>

                {/* Brand Section */}
                <div className="mt-4">
                  <h1
                    onClick={() => setBrand(!brand)}
                    className="flex justify-between cursor-pointer text-white text-lg"
                    aria-label="Toggle Trade by Brand"
                  >
                    Trade by Brand <span>{brand ? "-" : "+"}</span>
                  </h1>

                  {brand && (
                    <motion.ul
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-white ml-2 mt-2"
                    >
                      <li>
                        <Link to="/" onClick={() => setSidenav(false)}>
                          New Arrivals
                        </Link>
                      </li>
                      <li>
                        <Link to="/" onClick={() => setSidenav(false)}>
                          Others
                        </Link>
                      </li>
                    </motion.ul>
                  )}
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setSidenav(false)}
                  className="absolute top-4 right-4 w-8 h-8 text-white border border-gray-300 flex justify-center items-center rounded"
                  aria-label="Close navigation menu"
                >
                  <MdClose />
                </button>

              </motion.div>
            </div>
          )}
        </Flex>
      </nav>
    </header>
  );
};

export default Header;
